import { LEVELS } from './logger-factory';
import {
    ConsoleTransport,
    IndexedDBTransport,
    DesktopSdkTransport,
    ExternalApiTransport,
    DetectSensitiveData,
} from './transports';
import { mapObj, filterObj, isIdbAvailable } from './utils';
import './types';

const getAllowedTransportOpts = name => {
    switch (name) {
        case 'idb':
            return ['entryLifespan', 'entriesLimitPerStorage'];
        case 'console':
            return ['devToolsTheme'];
        default:
            return [];
    }
};

/**
 * Picks only allowed options from the transport config part by its name
 * @param {string} name
 * @param {Object} opts
 * @returns {Object} object containing the filtered keys
 */
export const pickTransportOpts = (name, opts) => {
    const allowedKeys = getAllowedTransportOpts(name);
    return filterObj(optName => allowedKeys.includes(optName), opts);
};

/**
 * Picks levels from the config.transports
 * @param {Object} configPart - transports config part
 * @returns {Object.<string, string>} - levels by transport name
 */
export const getTransportLevels = configPart =>
    mapObj(
        (name, opts) => opts.level,
        filterObj((name, opts) => Boolean(opts), configPart)
    );

export const storeNames = Object.keys(LEVELS).filter(lvl => lvl !== 'off');

const createTransport = (name, opts, externalAPI = {}) => {
    switch (name) {
        case 'console':
            return ConsoleTransport.create(pickTransportOpts(name, opts));
        case 'desktopSdk':
            return typeof externalAPI.desktopLog === 'function'
                ? new DesktopSdkTransport(pickTransportOpts(name, opts), externalAPI.desktopLog)
                : null;
        case 'externalApi':
            return typeof externalAPI.emit === 'function'
                ? new ExternalApiTransport(pickTransportOpts(name, opts), externalAPI.emit)
                : null;
        case 'idb':
            return isIdbAvailable()
                ? IndexedDBTransport.getInstance({
                      storeNames,
                      ...pickTransportOpts(name, opts),
                  })
                : null;
        case 'pii':
            return typeof externalAPI.detectSensitiveData === 'function'
                ? new DetectSensitiveData(externalAPI.detectSensitiveData)
                : null;
        default:
            return null;
    }
};

/** Translates rwc config to initialization options of logger factory */
export const mapConfigToLoggerOpts = ({ transports, level, prefixes }, externalAPI) => {
    const validTransportConfigs = filterObj((name, opts) => Boolean(opts), transports);
    const createdTransports = mapObj(
        (name, opts) => createTransport(name, opts, externalAPI),
        validTransportConfigs
    );

    return {
        level,
        prefixes,
        transports: filterObj((name, transport) => name && transport, createdTransports),
        transportLevels: getTransportLevels(validTransportConfigs),
    };
};

/** Translates logger options to rwc config format */
export const mapLoggerOptsToConfig = logFactory => ({
    level: logFactory.getLevel(),
    transports: mapObj(
        (name, transport) => ({
            ...pickTransportOpts(name, transport.getRuntimeOpts()),
            level: logFactory.getTransportLevel(name),
        }),
        logFactory.getTransports()
    ),
    prefixes: logFactory.getPrefixes(),
});
